import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  IconButton,
  InputAdornment,
  Typography,
  Tooltip,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  Refresh,
  Wifi,
  Security,
  Dns,
  Terminal,
  SettingsEthernet,
  Description as DescriptionIcon
} from "@mui/icons-material";
import { useForm, Controller } from 'react-hook-form';
import { nasService } from "../../services/nasService";
import { Nas, NasFormData } from "../../types";
import EscritorioSelect from "../selects/EscritorioSelect";

interface NasFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  nas?: Nas | null;
}

interface FormData {
  nasname: string;
  shortname: string;
  type: string;
  secret: string;
  community: string;
  description: string;
  enable_api: boolean;
  api_port: number | '';
  disabled_secret_api: boolean;
  usuario: string;
  senha: string;
  escritorio_id: number | '';
}

const NasForm: React.FC<NasFormProps> = ({ open, onClose, onSuccess, nas }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEdit = !!nas;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      nasname: '',
      shortname: '',
      type: 'other',
      secret: '',
      community: '',
      description: 'RADIUS Client',
      enable_api: false,
      api_port: '',
      disabled_secret_api: false,
      usuario: '',
      senha: '',
      escritorio_id: '',
    },
  });

  // Carregar dados do NAS para edição
  useEffect(() => {
    if (nas) {
      reset({
        nasname: nas.nasname,
        shortname: nas.shortname || '',
        type: nas.type || 'other',
        secret: nas.secret || '',
        community: nas.community || '',
        description: nas.description || 'RADIUS Client',
        enable_api: nas.enable_api || false,
        api_port: nas.api_port || '',
        disabled_secret_api: nas.disabled_secret_api || false,
        usuario: nas.usuario || '',
        senha: nas.senha || '',
        escritorio_id: nas.escritorio_id || '',
      });
    } else {
      reset({
        nasname: '',
        shortname: '',
        type: 'other',
        secret: '',
        community: '',
        description: 'RADIUS Client',
        enable_api: false,
        api_port: '',
        disabled_secret_api: false,
        usuario: '',
        senha: '',
        escritorio_id: '',
      });
    }
    setError(null);
  }, [nas, reset, open]);



  const onSubmit = handleSubmit(async (data) => {
    setLoading(true);
    setError(null);

    try {
      const formData: NasFormData = {
        nasname: data.nasname,
        shortname: data.shortname,
        type: data.type,
        secret: data.secret,
        community: data.community,
        description: data.description,
        enable_api: data.enable_api,
        api_port: data.api_port === '' ? undefined : Number(data.api_port),
        disabled_secret_api: data.disabled_secret_api,
        usuario: data.usuario,
        senha: data.senha,
        escritorio_id: data.escritorio_id === '' ? undefined : Number(data.escritorio_id),
      };

      if (isEdit && nas) {
        await nasService.update(nas.id, formData);
      } else {
        await nasService.create(formData);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Erro ao salvar NAS:', err);
      setError(err.response?.data?.message || 'Erro ao salvar equipamento NAS');
    } finally {
      setLoading(false);
    }
  });

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const getValidationRules = (field: string) => {
    switch (field) {
      case 'nasname':
        return {
          required: 'IP/Hostname é obrigatório',
          pattern: {
            value: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^[a-zA-Z0-9][a-zA-Z0-9\-\.]*[a-zA-Z0-9]$/,
            message: 'Formato inválido de IP ou hostname'
          }
        };
      case 'secret':
        return {
          required: 'Secret é obrigatório',
          minLength: {
            value: 6,
            message: 'Secret deve ter pelo menos 6 caracteres'
          }
        };
      case 'api_port':
        return {
          required: watch('enable_api') ? 'Porta API é obrigatória quando API está habilitada' : false,
          min: {
            value: 1,
            message: 'Porta API deve ser maior que 0'
          },
          max: {
            value: 65535,
            message: 'Porta API deve ser menor que 65536'
          }
        };
      default:
        return {};
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Wifi color="primary" />
            <Typography variant="h6">
              {isEdit ? 'Editar Equipamento NAS' : 'Novo Equipamento NAS'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={onSubmit}>
        <DialogContent dividers sx={{ bgcolor: '#fbfbfb' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* --- SEÇÃO: INFORMAÇÕES GERAIS --- */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Dns color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  Informações Gerais
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="shortname"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome do Equipamento"
                    fullWidth
                    placeholder="Ex: MikroTik Central"
                    size="small"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Controller
                name="nasname"
                control={control}
                rules={getValidationRules('nasname')}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="IP / Hostname"
                    fullWidth
                    error={!!errors.nasname}
                    helperText={errors.nasname?.message}
                    placeholder="192.168.1.1 ou nas.exemplo.com"
                    size="small"
                  />
                )}
              />
            </Grid>


            <Grid item xs={12} md={3}>
              <Controller
                name="secret"
                control={control}
                rules={getValidationRules('secret')}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Chave Secret"
                    fullWidth
                    error={!!errors.secret}
                    helperText={errors.secret?.message}
                    placeholder="Sua chave secret (min 6 caracteres)"
                    size="small"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Equipamento</InputLabel>
                    <Select {...field} label="Tipo de Equipamento">
                      <MenuItem value="accel_ppp">Accel-PPP</MenuItem>
                      <MenuItem value="mikrotik">MikroTik</MenuItem>
                      <MenuItem value="other">Outro</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={7}>
              <Controller
                name="escritorio_id"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <EscritorioSelect
                    value={value || undefined}
                    onChange={(escritorioId) => onChange(escritorioId)}
                    label="Escritório"
                    placeholder="Selecione um escritório"
                    onlyActive={true}
                    size="small"
                  />
                )}
              />
            </Grid>

            {/* --- SEÇÃO: API CONFIG --- */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} mt={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SettingsEthernet color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    Integração via API
                  </Typography>
                </Box>
                <Controller
                  name="enable_api"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={value}
                          onChange={(e) => onChange(e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">{value ? 'Habilitada' : 'Desabilitada'}</Typography>}
                    />
                  )}
                />
              </Box>
            </Grid>

            {watch('enable_api') && (
              <>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="usuario"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Usuário API"
                        fullWidth
                        placeholder="admin"
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="senha"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Senha API"
                        fullWidth
                        type="password"
                        placeholder="********"
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="api_port"
                    control={control}
                    rules={getValidationRules('api_port')}
                    render={({ field: { value, onChange, ...field } }) => (
                      <TextField
                        {...field}
                        value={value === '' ? '' : value}
                        onChange={(e) => {
                          const val = e.target.value;
                          onChange(val === '' ? '' : parseInt(val) || '');
                        }}
                        label="Porta API"
                        type="number"
                        fullWidth
                        placeholder="8728"
                        error={!!errors.api_port}
                        helperText={errors.api_port?.message}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} display="flex" alignItems="center">
                  <Controller
                    name="disabled_secret_api"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={value}
                            onChange={(e) => onChange(e.target.checked)}
                            color="warning"
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">Adicionar Secrets Desabilitados</Typography>}
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            {/* --- SEÇÃO: MONITORAMENTO --- */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1} mb={1} mt={1}>
                <Security color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  Monitoramento (SNMP)
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="community"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="SNMP Community"
                    fullWidth
                    placeholder="public"
                    size="small"
                  />
                )}
              />
            </Grid>

            {/* --- SEÇÃO: DESCRIÇÃO --- */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1} mb={1} mt={1}>
                <DescriptionIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  Anotações
                </Typography>
              </Box>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Observações Internas"
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Detalhes adicionais sobre este NAS..."
                    size="small"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {loading ? 'Salvando...' : (isEdit ? 'Atualizar' : 'Criar')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default NasForm; 